/**
 * WorkflowPolicyPage — 工作流策略管理
 *
 * CRUD for workflow policies that govern operational rules:
 * approval thresholds, mandatory fields, skip conditions, blocking rules, notifications.
 * Stored in `platform_settings` with key prefix `workflow:`.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Workflow, Plus, Trash2, RefreshCw, Loader2, X } from 'lucide-react';
import AdminTenantScopeBar from '@/components/AdminTenantScopeBar';
import { api } from '@/lib/api';
import { toast } from 'sonner';

interface WorkflowPolicy {
  key: string;
  policyKey: string;
  policyType: string;
  scope: string;
  scopeId?: string;
  config: Record<string, unknown>;
  description?: string;
  enabled: boolean;
  updated_at: string;
}

const POLICY_TYPES = [
  'approval_threshold',
  'mandatory_field',
  'skip_condition',
  'blocking_rule',
  'notification',
] as const;

const SCOPES = ['global', 'domain', 'tenant'] as const;

export default function WorkflowPolicyPage() {
  const [policies, setPolicies] = useState<WorkflowPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scopeTenantId, setScopeTenantId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    policyKey: '',
    policyType: 'approval_threshold' as string,
    scope: 'global' as string,
    scopeId: '',
    config: '{}',
    description: '',
    enabled: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<WorkflowPolicy[]>('/platform/workflow-policies');
      setPolicies(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = scopeTenantId
    ? policies.filter(p => p.scope === 'tenant' && p.scopeId === scopeTenantId)
    : policies;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.policyKey) {
      toast.error('Policy key is required');
      return;
    }
    let configObj: Record<string, unknown>;
    try {
      configObj = JSON.parse(form.config);
    } catch {
      toast.error('Config must be valid JSON');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/platform/workflow-policies', {
        policyKey: form.policyKey,
        policyType: form.policyType,
        scope: form.scope,
        scopeId: form.scopeId || undefined,
        config: configObj,
        description: form.description || undefined,
        enabled: form.enabled,
      });
      toast.success('Workflow policy created');
      setShowForm(false);
      setForm({ policyKey: '', policyType: 'approval_threshold', scope: 'global', scopeId: '', config: '{}', description: '', enabled: true });
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Create failed');
    }
    setSubmitting(false);
  };

  const handleDelete = async (key: string) => {
    if (!confirm(`Delete workflow policy "${key}"?`)) return;
    try {
      await api.delete(`/platform/workflow-policies/${encodeURIComponent(key)}`);
      toast.success('Deleted');
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Workflow className="w-6 h-6 text-indigo-600" />
          <h1 className="text-xl font-bold text-gray-800">Workflow Policies</h1>
        </div>
        <div className="flex items-center gap-2">
          <AdminTenantScopeBar value={scopeTenantId} onChange={setScopeTenantId} showRingBadge />
          <button onClick={load} className="p-2 rounded hover:bg-gray-100" title="Refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowForm(v => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700"
          >
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? 'Cancel' : 'New Policy'}
          </button>
        </div>
      </div>

      {/* Create form */}
      {showForm && (
        <form onSubmit={handleCreate} className="mb-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Policy Key *</label>
              <input
                value={form.policyKey}
                onChange={e => setForm(f => ({ ...f, policyKey: e.target.value }))}
                className="w-full px-2.5 py-1.5 border rounded text-sm"
                placeholder="e.g. max_invoice_amount"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
              <select
                value={form.policyType}
                onChange={e => setForm(f => ({ ...f, policyType: e.target.value }))}
                className="w-full px-2.5 py-1.5 border rounded text-sm"
              >
                {POLICY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Scope</label>
              <select
                value={form.scope}
                onChange={e => setForm(f => ({ ...f, scope: e.target.value }))}
                className="w-full px-2.5 py-1.5 border rounded text-sm"
              >
                {SCOPES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Scope ID (if domain/tenant)</label>
              <input
                value={form.scopeId}
                onChange={e => setForm(f => ({ ...f, scopeId: e.target.value }))}
                className="w-full px-2.5 py-1.5 border rounded text-sm"
                placeholder="tenant-id or domain name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <input
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                className="w-full px-2.5 py-1.5 border rounded text-sm"
                placeholder="optional description"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Config (JSON)</label>
            <textarea
              value={form.config}
              onChange={e => setForm(f => ({ ...f, config: e.target.value }))}
              className="w-full px-2.5 py-1.5 border rounded text-sm font-mono"
              rows={3}
              placeholder='{"threshold": 5000}'
            />
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))}
              />
              Enabled
            </label>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-1.5 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5"
            >
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Create Policy
            </button>
          </div>
        </form>
      )}

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{error}</div>
      )}

      {/* Loading */}
      {loading && !policies.length && (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      )}

      {/* Table */}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-12 text-gray-400">No workflow policies found</div>
      )}

      {filtered.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">Policy Key</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Scope</th>
                <th className="px-4 py-3">Enabled</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3 w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map(p => (
                <tr key={p.key} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-mono text-xs">{p.policyKey}</td>
                  <td className="px-4 py-2.5">
                    <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs">
                      {p.policyType}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="text-gray-600">{p.scope}</span>
                    {p.scopeId && <span className="ml-1 text-gray-400 text-xs">({p.scopeId})</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    {p.enabled
                      ? <span className="text-green-600 font-semibold text-xs">ON</span>
                      : <span className="text-gray-400 text-xs">OFF</span>}
                  </td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs truncate max-w-[200px]">{p.description || '—'}</td>
                  <td className="px-4 py-2.5">
                    <button
                      onClick={() => handleDelete(p.key)}
                      className="p-1 rounded text-red-400 hover:text-red-600 hover:bg-red-50"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
